import Button from "@/common/components/Button";
import { DashboardComponentChartProps } from "@/features/company/types/dashboards/dashboardComponent.type";
import { ListChevronsDownUpIcon, ListChevronsUpDownIcon } from "lucide-react";
import { useId, useState } from "react";
import TableRow from "./components/TableRow";

const getRowLevel = (row: any): number =>
  row.meta_attributes?.row_stack?.filter((value: any, index: number) => value && index)?.length || 0;

const TableComponent = ({
  chartData,
}: DashboardComponentChartProps) => {
  const uniqueId = useId();

  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

  const rows = chartData?.data ?? [];

  const getParentIndex = (rowIndex: number): number | null => {
    const level = getRowLevel(rows[rowIndex]);

    if (level === 0)
      return null;

    for (let i = rowIndex - 1; i >= 0; i--) {
      if (getRowLevel(rows[i]) === level - 1)
        return i;
    }

    return null;
  };

  const isRowVisible = (rowIndex: number): boolean => {
    if (getRowLevel(rows[rowIndex]) === 0)
      return true;

    const parentIndex = getParentIndex(rowIndex);

    if (parentIndex === null)
      return false;

    return expandedRows.has(parentIndex) && isRowVisible(parentIndex);
  };

  const rowHasChildren = (rowIndex: number): boolean => {
    const nextRow = rows[rowIndex + 1];

    if (!nextRow)
      return false;

    return getRowLevel(nextRow) > getRowLevel(rows[rowIndex]);
  };

  const toggleRow = (index: number) => {
    setExpandedRows(prev => {
      const next = new Set(prev);

      if (next.has(index))
        next.delete(index);
      else
        next.add(index);

      return next;
    });
  };

  const expandableIndices = rows.reduce<number[]>((acc, _, index) => {
    if (rowHasChildren(index))
      acc.push(index);

    return acc;
  }, []);

  const allExpanded = expandableIndices.length > 0 && expandableIndices.every(i => expandedRows.has(i));

  const toggleAll = () => {
    if (allExpanded) {
      setExpandedRows(new Set());
    } else {
      setExpandedRows(new Set(expandableIndices));
    }
  };

  return (
    <div className="w-full h-full overflow-auto thin-scroll">
      <table className="w-full">
        <thead className="sticky top-0 z-10 bg-neutral-800">
          <tr>
            <th className="border border-t-0 border-l-0 border-black-300 text-left text-xs p-2">
              {expandableIndices?.length > 0 && (
                <Button variant="link-white" className="text-[11px] font-normal p-1 -m-1 transition-colors rounded hover:bg-white-100" onClick={toggleAll}>
                  {allExpanded ? (
                    <ListChevronsDownUpIcon strokeWidth={1.5} size={16} className="-scale-x-100" />
                  ) : (
                    <ListChevronsUpDownIcon strokeWidth={1.5} size={16} className="-scale-x-100" />
                  )}

                  {allExpanded ? 'Colapsar todos' : 'Expandir todos'}
                </Button>
              )}
            </th>

            {chartData?.series_settings && Object.entries(chartData?.series_settings)?.map(([key, value]) => (
              <td key={key} className="border border-t-0 border-r-0 border-black-300 text-left text-xs p-2">
                {value.label}
              </td>
            ))}
          </tr>
        </thead>

        <tbody>
          {rows.map((row, index) => {
            if (!isRowVisible(index)) return null;
            return (
              <TableRow
                key={`${uniqueId}.${index}`}
                uniqueId={uniqueId}
                index={index}
                row={row}
                primarySettings={chartData.primary_settings}
                seriesSettings={chartData.series_settings}
                isExpanded={expandedRows.has(index)}
                hasChildren={rowHasChildren(index)}
                onToggle={() => toggleRow(index)}
              />
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default TableComponent;
